import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { LayoutDashboard, Users, ShieldAlert, DollarSign, Package, Settings2, Rocket, BarChart3, CheckCircle2, XCircle, Clock, Ban, Heart } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { cn, PageLoading, Spinner } from "@/components/common";
import AdminDeals from "@/pages/AdminDeals";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "trust", label: "Trust & Safety", icon: ShieldAlert },
  { id: "moderation", label: "Moderation", icon: Package },
  { id: "users", label: "Users", icon: Users },
  { id: "revenue", label: "Revenue", icon: DollarSign },
  { id: "monetisation", label: "Monetisation", icon: Heart },
  { id: "products", label: "Pricing", icon: DollarSign },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "settings", label: "Settings", icon: Settings2 },
  { id: "launch", label: "Launch", icon: Rocket },
];

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-2xl font-extrabold">{value}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

const money = (cents) => `$${((cents || 0) / 100).toLocaleString("en-AU", { minimumFractionDigits: 2 })}`;

function Monetisation() {
  const { user } = useAuth();
  const [sub, setSub] = useState("support");
  const [summary, setSummary] = useState(null);
  const [contribs, setContribs] = useState([]);
  const [featured, setFeatured] = useState({ purchases: [], active: [] });
  const [sponsored, setSponsored] = useState([]);
  const [packages, setPackages] = useState([]);
  const [editing, setEditing] = useState(null);
  const isOwner = ["owner", "admin"].includes(user?.role);
  const load = () => {
    api.get("/admin/monetisation/summary").then(({ data }) => setSummary(data)).catch(() => {});
    api.get("/admin/monetisation/contributions").then(({ data }) => setContribs(data.items)).catch(() => {});
    api.get("/admin/monetisation/featured").then(({ data }) => setFeatured(data)).catch(() => {});
    api.get("/admin/monetisation/sponsored").then(({ data }) => setSponsored(data.items)).catch(() => {});
    api.get("/admin/monetisation/featured-packages").then(({ data }) => setPackages(data.items)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const savePkg = async () => {
    try {
      const body = { name: editing.name, price: Math.round(parseFloat(editing.priceDollars) * 100), currency: "aud", duration_days: parseInt(editing.duration_days, 10), active: !!editing.active };
      if (editing.id) await api.put(`/admin/monetisation/featured-packages/${editing.id}`, body);
      else await api.post("/admin/monetisation/featured-packages", body);
      toast.success("Package saved"); setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  if (!summary) return <Spinner />;
  const SUBS = [["support", "Support Mtaa"], ["featured", "Featured Listings"], ["sponsored", "Sponsored Ads"], ["deals", "Mtaa Deals"]];
  return (
    <div className="space-y-4" data-testid="admin-monetisation">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Support Mtaa" value={money(summary.support.total)} sub={`${summary.support.count} contributions`} />
        <Stat label="Featured Listings" value={money(summary.featured.total)} sub={`${summary.featured.count} purchases`} />
        <Stat label="Sponsored Ads" value={money(summary.sponsored.total)} sub={`${summary.sponsored.count} paid`} />
      </div>
      <div className="flex gap-1.5">
        {SUBS.map(([id, label]) => (
          <button key={id} onClick={() => setSub(id)} data-testid={`mon-sub-${id}`}
            className={cn("rounded-full px-3 py-1.5 text-sm font-semibold", sub === id ? "bg-primary text-white" : "bg-secondary text-muted-foreground")}>{label}</button>
        ))}
      </div>

      {sub === "support" && (
        <section className="space-y-2">
          <h2 className="font-display font-semibold">Contributions</h2>
          {contribs.length === 0 ? <p className="text-sm text-muted-foreground">No contributions yet.</p> : contribs.map((c) => (
            <div key={c.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-3 text-sm" data-testid={`contrib-${c.id}`}>
              <span className="text-muted-foreground">{new Date(c.created_at).toLocaleDateString("en-AU")} · user {String(c.user_id || "").slice(0, 8)}</span>
              <span className="font-medium">{money(c.amount)} · {c.payment_status}</span>
            </div>
          ))}
        </section>
      )}

      {sub === "featured" && (
        <div className="space-y-4">
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-semibold">Packages</h2>
              {isOwner && <Button size="sm" variant="outline" onClick={() => setEditing({ name: "", priceDollars: "", duration_days: 7, active: true })} data-testid="pkg-new">New package</Button>}
            </div>
            {packages.map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-3 text-sm" data-testid={`pkg-${p.id}`}>
                <span>{p.name} · {p.duration_days}d · <b>{money(p.price)}</b> {!p.active && <span className="text-muted-foreground">(inactive)</span>}</span>
                {isOwner && <Button size="sm" variant="ghost" onClick={() => setEditing({ ...p, priceDollars: (p.price / 100).toFixed(2) })} data-testid={`pkg-edit-${p.id}`}>Edit</Button>}
              </div>
            ))}
          </section>
          <section className="space-y-2">
            <h2 className="font-display font-semibold">Active featured listings</h2>
            {featured.active.length === 0 ? <p className="text-sm text-muted-foreground">No active featured listings.</p> : featured.active.map((l) => (
              <div key={l.id} className="rounded-xl border border-border bg-card p-3 text-sm" data-testid={`feat-active-${l.id}`}>
                <p className="font-medium">{l.title}</p>
                <p className="text-xs text-muted-foreground">Until {new Date(l.featured_until).toLocaleDateString("en-AU")} · {l.featured_stats?.impressions || 0} impressions · {l.featured_stats?.views || 0} views · {l.featured_stats?.clicks || 0} clicks</p>
              </div>
            ))}
          </section>
        </div>
      )}

      {sub === "sponsored" && (
        <section className="space-y-2">
          <h2 className="font-display font-semibold">Sponsored / promotions</h2>
          <p className="text-xs text-muted-foreground">Business promotions, boosts and subscriptions.</p>
          {sponsored.length === 0 ? <p className="text-sm text-muted-foreground">No sponsored transactions yet.</p> : sponsored.map((s) => (
            <div key={s.session_id} className="flex items-center justify-between rounded-xl border border-border bg-card p-3 text-sm">
              <span>{(s.lookup_key || "").replace(/_/g, " ")}</span>
              <span className="font-medium">{money(s.amount)} · {s.payment_status}</span>
            </div>
          ))}
        </section>
      )}

      {sub === "deals" && <AdminDeals />}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="pkg-edit-modal">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit package" : "New package"}</DialogTitle>
            <DialogDescription>Configure the package name, price (AUD), duration and status. Pricing can be changed anytime without code changes.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><label className="text-sm">Name</label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="pkg-name" /></div>
              <div className="flex gap-2">
                <div className="flex-1"><label className="text-sm">Price (AUD $)</label><Input type="number" value={editing.priceDollars} onChange={(e) => setEditing({ ...editing, priceDollars: e.target.value })} data-testid="pkg-price" /></div>
                <div className="flex-1"><label className="text-sm">Duration (days)</label><Input type="number" value={editing.duration_days} onChange={(e) => setEditing({ ...editing, duration_days: e.target.value })} data-testid="pkg-duration" /></div>
              </div>
              <label className="flex items-center justify-between text-sm"><span>Active</span><Switch checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} data-testid="pkg-active" /></label>
            </div>
          )}
          <DialogFooter className="flex-row gap-2">
            <Button variant="outline" className="flex-1" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="flex-1" onClick={savePkg} data-testid="pkg-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  if (!user) return <PageLoading />;
  if (!["owner", "admin", "moderator"].includes(user.role))
    return <div className="py-20 text-center text-muted-foreground">You don't have access to the admin area.</div>;
  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Owner dashboard</h1>
        <p className="text-sm text-muted-foreground">Signed in as {user.email} · {user.role}</p>
      </div>
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar border-b border-border pb-2">
        {TABS.map((t) => { const Icon = t.icon; return (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
            className={cn("flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors", tab === t.id ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground")}>
            <Icon className="h-4 w-4" />{t.label}
          </button>
        ); })}
      </div>
      {tab === "overview" && <Overview />}
      {tab === "trust" && <TrustSafety />}
      {tab === "moderation" && <Moderation />}
      {tab === "users" && <UsersTab />}
      {tab === "revenue" && <Revenue />}
      {tab === "monetisation" && <Monetisation />}
      {tab === "products" && <Products />}
      {tab === "analytics" && <Analytics />}
      {tab === "settings" && <SettingsTab />}
      {tab === "launch" && <Launch />}
    </div>
  );
}

function Overview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/overview").then(({ data }) => setD(data)); }, []);
  if (!d) return <Spinner />;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Total users" value={d.users.total} sub={`+${d.users.new_this_week} this week`} />
        <Stat label="Active today" value={d.users.active_today} />
        <Stat label="Businesses" value={d.content.businesses} sub={`${d.content.verified_businesses} verified`} />
        <Stat label="Listings" value={d.content.marketplace_listings} />
        <Stat label="Revenue today" value={`$${d.revenue.today}`} />
        <Stat label="This week" value={`$${d.revenue.this_week}`} />
        <Stat label="This month" value={`$${d.revenue.this_month}`} />
        <Stat label="Subscriptions" value={d.revenue.active_subscriptions} />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Pending reports" value={d.queues.pending_reports} />
        <Stat label="Pending verification" value={d.queues.pending_verification} />
        <Stat label="High-risk alerts" value={d.queues.high_risk_alerts} />
      </div>
    </div>
  );
}

function Revenue() {
  const [d, setD] = useState(null);
  const [target, setTarget] = useState("");
  useEffect(() => { api.get("/admin/overview").then(({ data }) => { setD(data); setTarget(data.revenue_target.target); }); }, []);
  if (!d) return <Spinner />;
  const rt = d.revenue_target;
  const pct = Math.min(100, Math.round((rt.current / rt.target) * 100));
  const saveTarget = async () => { try { await api.post("/admin/settings/value", { key: "weekly_revenue_target", value: parseFloat(target) }); toast.success("Target updated"); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-border bg-card p-6">
        <p className="text-sm text-muted-foreground">Weekly revenue target (private)</p>
        <p className="mt-1 font-display text-4xl font-extrabold text-foreground">${rt.current.toLocaleString()} <span className="text-lg font-medium text-brand-green">/ ${rt.target.toLocaleString()}</span></p>
        <div className="mt-3"><Progress value={pct} className="h-3" /></div>
        <p className="mt-2 text-sm text-brand-green">{pct}% of weekly goal</p>
      </div>
      <div className="rounded-2xl border border-border bg-card p-4">
        <p className="font-display font-semibold">Revenue breakdown (this week)</p>
        <div className="mt-2 space-y-1 text-sm">
          {Object.keys(rt.breakdown).length === 0 ? <p className="text-muted-foreground">No revenue this week yet.</p> :
            Object.entries(rt.breakdown).map(([k, v]) => <div key={k} className="flex justify-between border-b border-border py-1 last:border-0"><span className="capitalize">{k.replace(/_/g, " ")}</span><span className="font-medium">${v}</span></div>)}
        </div>
      </div>
      <div className="flex items-end gap-2">
        <div className="flex-1"><label className="text-sm font-medium">Set weekly target ($)</label><Input type="number" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="admin-target-input" /></div>
        <Button onClick={saveTarget} data-testid="admin-target-save">Save</Button>
      </div>
    </div>
  );
}

function TrustSafety() {
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState(null);
  const [counts, setCounts] = useState({});
  const [ts, setTs] = useState(null);
  const [detail, setDetail] = useState(null);
  const [confirm, setConfirm] = useState(null); // {action,label,destructive}
  const [reason, setReason] = useState("");
  const loadList = () => api.get("/admin/reports", { params: { status: tab } }).then(({ data }) => { setData(data.items); setCounts(data.counts); });
  const loadTs = () => api.get("/admin/trust-safety").then(({ data }) => setTs(data));
  useEffect(() => { loadList(); }, [tab]);
  useEffect(() => { loadTs(); }, []);

  const openDetail = (rid) => api.get(`/admin/reports/${rid}`).then(({ data }) => { setDetail(data); loadList(); });
  const runAction = async () => {
    try {
      await api.post(`/admin/reports/${detail.report.id}/action`, { action: confirm.action, reason });
      toast.success("Action recorded");
      setConfirm(null); setReason("");
      const rid = detail.report.id; setDetail(null);
      await api.get(`/admin/reports/${rid}`).then(({ data }) => setDetail(data)).catch(() => {});
      loadList(); loadTs();
    } catch (e) { toast.error(apiError(e)); }
  };

  const STATUS_STYLE = { open: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300", under_review: "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300", actioned: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300", dismissed: "bg-secondary text-muted-foreground" };
  const FILTERS = ["pending", "open", "under_review", "actioned", "dismissed", "all"];

  const ACTIONS = {
    dismiss: { label: "Dismiss report" },
    no_action: { label: "No action required" },
    warn_user: { label: "Warn user" },
    remove_content: { label: "Remove reported content", destructive: true },
    restore_content: { label: "Restore content" },
    suspend_user: { label: "Suspend user", destructive: true },
    unsuspend_user: { label: "Unsuspend user" },
    ban_user: { label: "Ban user", destructive: true },
    unban_user: { label: "Unban user" },
  };

  const computeActions = (d) => {
    const keys = ["dismiss", "no_action"];
    const ru = d.reported_user;
    if (ru) {
      keys.push("warn_user");
      if (ru.is_banned) keys.push("unban_user");
      else if (ru.is_suspended) { keys.push("unsuspend_user"); keys.push("ban_user"); }
      else { keys.push("suspend_user"); keys.push("ban_user"); }
    }
    if (d.content_moderatable) {
      if (d.content_status === "removed") keys.push("restore_content");
      else keys.push("remove_content");
    }
    return keys;
  };

  const userStatus = (ru) => ru?.is_banned ? "BANNED" : ru?.is_suspended ? "SUSPENDED" : "ACTIVE";
  const STATUS_BADGE = { ACTIVE: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300", SUSPENDED: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300", BANNED: "bg-destructive/10 text-destructive", removed: "bg-destructive/10 text-destructive", active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300" };


  return (
    <div className="space-y-5">
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-display font-semibold">Reports</h2>
        </div>
        <div className="mb-3 flex gap-1.5 overflow-x-auto no-scrollbar">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setTab(f)} data-testid={`report-filter-${f}`}
              className={cn("shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold capitalize", tab === f ? "bg-primary text-white" : "bg-secondary text-muted-foreground")}>
              {f.replace("_", " ")} {counts[f] != null ? `(${counts[f]})` : ""}
            </button>
          ))}
        </div>
        <div className="space-y-2">
          {data === null ? <Spinner /> : data.length === 0 ? <p className="text-sm text-muted-foreground">No reports in this view.</p> : data.map((r) => (
            <div key={r.id} onClick={() => openDetail(r.id)} data-testid={`report-card-${r.id}`}
              className="flex cursor-pointer items-center justify-between rounded-xl border border-border bg-card p-3 text-sm hover:border-primary">
              <div className="min-w-0">
                <span className={cn("mr-2 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase", STATUS_STYLE[r.status])}>{(r.status || "").replace("_", " ")}</span>
                <b>{r.reason}</b> · {r.target_type}
                <div className="truncate text-xs text-muted-foreground">{r.details || "No extra details"} · {new Date(r.created_at).toLocaleString("en-AU")}</div>
              </div>
              <Button size="sm" onClick={(e) => { e.stopPropagation(); openDetail(r.id); }} data-testid={`report-review-${r.id}`}>Review</Button>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="mb-2 font-display font-semibold">Risk events {ts ? `(${ts.risk_events.length})` : ""}</h2>
        <div className="space-y-2">
          {!ts ? <Spinner /> : ts.risk_events.length === 0 ? <p className="text-sm text-muted-foreground">No unresolved risk events.</p> : ts.risk_events.map((e) => (
            <div key={e.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-3 text-sm">
              <div><span className={cn("mr-2 rounded px-1.5 py-0.5 text-[10px] font-bold", ["HIGH", "CRITICAL"].includes(e.level) ? "bg-destructive/10 text-destructive" : "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300")}>{e.level}</span>{e.object_type} · score {e.score}{e.ai_used && " · AI"}<div className="text-xs text-muted-foreground">{(e.reasons || []).join(", ")}</div></div>
              <Button size="sm" variant="outline" onClick={() => api.post(`/admin/risk/${e.id}/resolve`).then(loadTs)} data-testid={`risk-resolve-${e.id}`}>Resolve</Button>
            </div>
          ))}
        </div>
      </section>
      <section>
        <h2 className="mb-2 font-display font-semibold">Verification queue {ts ? `(${ts.verification_queue.length})` : ""}</h2>
        {!ts ? null : ts.verification_queue.length === 0 ? <p className="text-sm text-muted-foreground">Nothing pending.</p> : ts.verification_queue.map((v) => <VerifRow key={v.id} v={v} reload={loadTs} />)}
      </section>

      {/* Report Details modal */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="report-detail-modal">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">Report Details
                  <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-bold uppercase", STATUS_STYLE[detail.report.status])} data-testid="report-detail-status">{(detail.report.status || "").replace("_", " ")}</span>
                </DialogTitle>
                <DialogDescription>ID {detail.report.id.slice(0, 8)} · {new Date(detail.report.created_at).toLocaleString("en-AU")}</DialogDescription>
              </DialogHeader>
              <div className="space-y-2 text-sm">
                <p><b>Reason:</b> {detail.report.reason}</p>
                <p><b>Reporter details:</b> {detail.report.details || "—"}</p>
                <div className="flex flex-wrap gap-2">
                  {detail.reporter && <a href={`/u/${detail.reporter.id}`} target="_blank" rel="noreferrer" className="rounded-lg border border-border px-2 py-1 text-xs hover:border-primary" data-testid="view-reporter">Reporter: {detail.reporter.name}</a>}
                  {detail.reported_user && <a href={`/u/${detail.reported_user.id}`} target="_blank" rel="noreferrer" className="rounded-lg border border-border px-2 py-1 text-xs hover:border-primary" data-testid="view-reported-user">Reported: {detail.reported_user.name}</a>}
                  {detail.content_link && <a href={detail.content_link} target="_blank" rel="noreferrer" className="rounded-lg border border-primary px-2 py-1 text-xs text-primary" data-testid="view-content">View reported content ↗</a>}
                </div>
                {detail.reported_user && (
                  <div className="flex items-center gap-2 rounded-lg bg-secondary/50 p-2 text-xs" data-testid="user-status-row">
                    <b>User status:</b>
                    <span className={cn("rounded px-1.5 py-0.5 font-bold uppercase", STATUS_BADGE[userStatus(detail.reported_user)])} data-testid="reported-user-status">{userStatus(detail.reported_user)}</span>
                  </div>
                )}
                {detail.content_moderatable && (
                  <div className="flex items-center gap-2 rounded-lg bg-secondary/50 p-2 text-xs" data-testid="content-status-row">
                    <b>{detail.content?.title || detail.content?.name || detail.content?.text || "Reported content"}</b> ·
                    <span>status:</span>
                    <span className={cn("rounded px-1.5 py-0.5 font-bold uppercase", STATUS_BADGE[detail.content_status] || "bg-secondary text-muted-foreground")} data-testid="content-status">{detail.content_status || "n/a"}</span>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">Previous reports on this content: {detail.previous_reports_on_content} · on this user: {detail.previous_reports_on_user} · risk alerts: {detail.risk_events.length}</p>
                {detail.history.length > 0 && (
                  <div className="rounded-lg border border-border p-2" data-testid="report-history">
                    <p className="mb-1 text-xs font-semibold">Moderation history</p>
                    {detail.history.map((h) => <p key={h.id} className="text-[11px] text-muted-foreground">{new Date(h.created_at).toLocaleString("en-AU")} — {h.actor_email}: {h.action.replace("_", " ")} ({h.prev_status}→{h.new_status}){h.reason ? ` · ${h.reason}` : ""}</p>)}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2 pt-2">
                  {computeActions(detail).map((key) => { const a = ACTIONS[key]; return (
                    <Button key={key} size="sm" variant={a.destructive ? "destructive" : "outline"}
                      onClick={() => { setConfirm({ action: key, ...a }); setReason(""); }} data-testid={`action-${key}`}>{a.label}</Button>
                  ); })}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Action confirm modal */}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid="action-confirm-modal">
          {confirm && (
            <>
              <DialogHeader>
                <DialogTitle>{confirm.label}?</DialogTitle>
                <DialogDescription>{confirm.destructive ? "This action affects the platform and requires a reason." : "Add an optional internal note."}</DialogDescription>
              </DialogHeader>
              <Input placeholder={confirm.destructive ? "Reason (required)" : "Internal note (optional)"} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="action-reason" />
              <DialogFooter className="flex-row gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setConfirm(null)} data-testid="action-cancel">Cancel</Button>
                <Button variant={confirm.destructive ? "destructive" : "default"} className="flex-1" onClick={runAction} data-testid="action-confirm">Confirm</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VerifRow({ v, reload }) {
  const decide = async (decision) => { try { await api.post(`/admin/verification/${v.id}/decision`, { decision, reason: decision }); toast.success("Done"); reload(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="flex items-center justify-between rounded-xl border border-border bg-card p-3 text-sm">
      <div>{v.type} verification · {v.registered_name || ""} {v.abn ? `ABN ${v.abn}` : ""}</div>
      <div className="flex gap-1"><Button size="sm" variant="outline" onClick={() => decide("reject")}>Reject</Button><Button size="sm" onClick={() => decide("approve")} data-testid={`verif-approve-${v.id}`}>Approve</Button></div>
    </div>
  );
}

function Moderation() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/moderation/queue").then(({ data }) => setD(data));
  useEffect(() => { load(); }, []);
  if (!d) return <Spinner />;
  const act = async (collection, id, action) => { try { await api.post(`/admin/content/${id}/moderate`, { collection, action, reason: action }); toast.success("Done"); load(); } catch (e) { toast.error(apiError(e)); } };
  const groups = [["businesses", d.businesses], ["marketplace", d.marketplace], ["services", d.services], ["requests", d.requests]];
  const empty = groups.every(([, items]) => items.length === 0);
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Items flagged HIGH/CRITICAL by the risk engine await your review before going public.</p>
      {empty && <p className="text-sm text-muted-foreground">Moderation queue is clear ✓</p>}
      {groups.map(([coll, items]) => items.length > 0 && (
        <section key={coll}>
          <h2 className="mb-2 font-display font-semibold capitalize">{coll} ({items.length})</h2>
          {items.map((it) => (
            <div key={it.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-3 text-sm">
              <div><b>{it.name || it.title}</b> · {it.category} · {it.state}</div>
              <div className="flex gap-1"><Button size="sm" variant="outline" onClick={() => act(coll, it.id, "reject")}>Reject</Button><Button size="sm" onClick={() => act(coll, it.id, "approve")} data-testid={`mod-approve-${it.id}`}>Approve</Button></div>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/users", { params: q ? { q } : {} }).then(({ data }) => setUsers(data.items));
  useEffect(() => { load(); }, []);
  const act = async (uid, action) => { const reason = action === "suspend" ? window.prompt("Reason for suspension:") : ""; if (action === "suspend" && !reason) return; try { await api.post(`/admin/users/${uid}/action`, { action, reason }); toast.success("Done"); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="space-y-3">
      <div className="flex gap-2"><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users…" data-testid="admin-user-search" /><Button onClick={load}>Search</Button></div>
      <div className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card">
        {users.map((u) => (
          <div key={u.id} className="flex items-center justify-between p-3 text-sm">
            <div><b>{u.name}</b> · {u.email}<div className="text-xs text-muted-foreground">{u.role} {u.phone_verified && "· phone ✓"} {u.is_suspended && "· SUSPENDED"}</div></div>
            <div className="flex gap-1">
              {u.is_suspended ? <Button size="sm" variant="outline" onClick={() => act(u.id, "unsuspend")}>Unsuspend</Button> : <Button size="sm" variant="destructive" onClick={() => act(u.id, "suspend")} data-testid={`user-suspend-${u.id}`}><Ban className="h-3.5 w-3.5" />Suspend</Button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Products() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/products").then(({ data }) => setItems(data.items));
  useEffect(() => { load(); }, []);
  const save = async (lk, amount) => { try { await api.put(`/admin/products/${lk}`, { amount: parseFloat(amount) }); toast.success("Price updated"); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">Prices are database-driven. Editing here updates checkout instantly (Stripe price stays linked).</p>
      {items.map((p) => (
        <div key={p.lookup_key} className="flex items-center justify-between rounded-xl border border-border bg-card p-3 text-sm">
          <div><b>{p.name}</b> <span className="text-muted-foreground">· {p.category}{p.interval ? `/${p.interval}` : ""}</span></div>
          <div className="flex items-center gap-2">
            <span>$</span><Input className="w-24" defaultValue={p.amount} onBlur={(e) => e.target.value != p.amount && save(p.lookup_key, e.target.value)} data-testid={`price-${p.lookup_key}`} />
          </div>
        </div>
      ))}
    </div>
  );
}

function Analytics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then(({ data }) => setD(data)); }, []);
  if (!d) return <Spinner />;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Total searches" value={d.total_searches} />
        <Stat label="Total messages" value={d.total_messages} />
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <Panel title="Top searches" rows={d.top_searches} render={(r) => [r._id, r.count]} />
        <Panel title="Zero-result searches" rows={d.zero_result_searches} render={(r) => [r._id, r.count]} />
        <Panel title="Top categories" rows={d.top_categories} render={(r) => [r._id, r.count]} />
        <Panel title="By state" rows={d.by_state} render={(r) => [r._id || "—", r.count]} />
      </div>
    </div>
  );
}
function Panel({ title, rows, render }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <p className="font-display font-semibold">{title}</p>
      <div className="mt-2 space-y-1 text-sm">
        {(!rows || rows.length === 0) ? <p className="text-muted-foreground">No data yet.</p> : rows.map((r, i) => { const [a, b] = render(r); return <div key={i} className="flex justify-between border-b border-border py-1 last:border-0"><span>{a}</span><span className="font-medium">{b}</span></div>; })}
      </div>
    </div>
  );
}

function SettingsTab() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/settings").then(({ data }) => setD(data));
  useEffect(() => { load(); }, []);
  if (!d) return <Spinner />;
  const toggle = async (key, value) => { try { await api.post("/admin/settings/flags", { key, value }); setD((p) => ({ ...p, feature_flags: { ...p.feature_flags, [key]: value } })); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">Feature flags let you safely enable/disable modules and emergency controls.</p>
      {Object.entries(d.feature_flags).map(([k, v]) => (
        <label key={k} className="flex items-center justify-between rounded-xl border border-border bg-card p-3 text-sm">
          <span className="capitalize">{k.replace(/_/g, " ")}</span>
          <Switch checked={v} onCheckedChange={(val) => toggle(k, val)} data-testid={`flag-${k}`} />
        </label>
      ))}
    </div>
  );
}

function Launch() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/launch-readiness").then(({ data }) => setD(data)); }, []);
  if (!d) return <Spinner />;
  const Icon = { pass: CheckCircle2, fail: XCircle, pending: Clock };
  const color = { pass: "text-emerald-600", fail: "text-destructive", pending: "text-amber-600" };
  return (
    <div className="space-y-3">
      <div className={cn("flex items-start gap-3 rounded-2xl border p-4", d.technical_ready ? "border-brand-green/30 bg-brand-green/10" : "border-border bg-card")}>
        <div className={cn("mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl", d.technical_ready ? "bg-brand-green/15 text-brand-green" : "bg-secondary text-muted-foreground")}>
          {d.technical_ready ? <CheckCircle2 className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
        </div>
        <div>
          <p className="font-display text-lg font-bold text-foreground">{d.technical_ready ? "Technical checks passed" : "Not ready to launch"}</p>
          <p className="text-sm text-muted-foreground">{d.note}</p>
        </div>
      </div>
      <div className="space-y-2">
        {d.checks.map((c) => { const I = Icon[c.status]; return (
          <div key={c.key} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3 text-sm">
            <I className={cn("h-5 w-5", color[c.status])} />
            <span className="flex-1">{c.label}</span>
            <span className="text-xs text-muted-foreground">{c.type === "owner" ? "Requires owner/professional confirmation" : c.status}</span>
          </div>
        ); })}
      </div>
    </div>
  );
}
